import { useEffect } from "react";
import useSWR from "@/hooks/useSWR";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCommittedLocation } from "@/routing/committedLocation";
import { stringifySearch } from "@/routing/searchParams";
import { SubuserType } from "@/types/SubuserTypes";
import WebshareApi from "../api";
import useSubuserId from "./useSubuserId";
import usePrevious from "./usePrevious";

/**
 * True when a fetched sub-user's own plan contradicts the plan context in the
 * URL. Named plan-type segments (/staticResidential/…) are skipped — they
 * aren't comparable to the numeric plan id the sub-user endpoint returns.
 */
function isForeignSubuserPlan(
  subuserPlanId: SubuserType["plan"] | undefined,
  currentPlanId: string | undefined
) {
  return (
    subuserPlanId != null &&
    currentPlanId != null &&
    /^\d+$/.test(String(currentPlanId)) &&
    String(subuserPlanId) !== String(currentPlanId)
  );
}

export default function useSubuser() {
  const navigate = useNavigate();
  /**
   * One clock: `useCommittedLocation` is the settled location Next published
   * (ANO-079) and `useSubuserId` reads through it, so path, query and id agree.
   * Off `state.location` the *pending* query pairs with the *committed* path,
   * and the href below would then be a URL neither left nor entered.
   * `useParams` is the exception — a path segment lives on the match, which
   * commits with the path — and feeds the comparison only, never the href.
   */
  const location = useCommittedLocation();
  const pathname = location.pathname;
  const search = location.search as Record<string, string>;
  const params = useParams({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const subUserIdQueryString = useSubuserId();
  const prevSubuserId = usePrevious(subUserIdQueryString);
  /**
   * Next's `router.query.planId`: the `$planId` segment over the query
   * parameter, the merge `useUrlRecord` does in `useUrlState`. Not
   * `usePlanId()`: it must return `PlanIdTypeSafe`, so `Number()` collapses
   * every non-numeric segment to `"default"` — and the named plan-type routes
   * are that shape (`/staticResidential/pricing`), so two different plans
   * compare equal, the guard below stops firing and the stale `X-Subuser`
   * header rides into the next plan.
   */
  const currentPlanId = params.planId ?? search.planId;
  const prevPlanId = usePrevious(currentPlanId);

  /**
   * We are going to optimistically set sub user id if present and has changed.
   */
  useEffect(() => {
    if (subUserIdQueryString !== prevSubuserId && subUserIdQueryString) {
      WebshareApi.setSubuserHeader(subUserIdQueryString);
    }
  }, [prevSubuserId, subUserIdQueryString]);

  const {
    data: subuserResponse,
    mutate: mutateSubuser,
    error: subuserError,
  } = useSWR<SubuserType>(
    {
      url: `/subuser/${subUserIdQueryString}/`,
      enabled: !!subUserIdQueryString,
    },
    {
      shouldRetryOnError: false,
    }
  );

  /**
   * Exit sub-user mode when the `subuser` param turns out to be invalid:
   *
   * - The fetched sub-user's plan contradicts a numeric plan context in the
   *   URL (R-1856): a sub-user belongs to exactly one plan, and the plan-
   *   change guard in the effect below can't fire when the context *lands* on
   *   a foreign plan from a plan-less page — the backend then 403s every
   *   plan-scoped request on its subuser/plan check.
   * - The sub-user fetch errored (e.g. a hand-edited query param); the header
   *   was added optimistically beforehand, so remove it again.
   *
   * Dropping the param matters in both cases — navigating to the unchanged
   * URL leaves the bad param in place and the subuser-scoped requests polling.
   */
  useEffect(() => {
    if (!subUserIdQueryString) return;
    const isForeign = isForeignSubuserPlan(
      subuserResponse?.plan,
      currentPlanId
    );
    if (!isForeign && !subuserError) return;

    WebshareApi.removeSubUserHeader();
    const { subuser: _subuser, ...searchWithoutSubuser } = search;
    void navigate({
      href: `${pathname}${stringifySearch(searchWithoutSubuser)}`,
    });
  }, [
    subuserResponse,
    subUserIdQueryString,
    search,
    pathname,
    navigate,
    subuserError,
    currentPlanId,
  ]);

  useEffect(() => {
    if (
      !subUserIdQueryString &&
      WebshareApi.getSubUserId() !== undefined &&
      pathname !== "/subuser"
    ) {
      /**
       * If the user navigated to a different plan, exit subuser mode.
       * Subusers belong to a specific plan, so viewing them under a
       * different plan would show incorrect data.
       */
      if (currentPlanId && prevPlanId && currentPlanId !== prevPlanId) {
        WebshareApi.removeSubUserHeader();
        return;
      }

      // Same route, different search (ANO-117). `resetScroll: false`: this push
      // was `{ shallow: true }` on the route it was already on, so Next's
      // `shouldScroll` was false and the page did not jump to the top. The error
      // branch above omits it deliberately — not shallow, so Next did scroll.
      void navigate({
        href: `${pathname}${stringifySearch({
          ...search,
          subuser: WebshareApi.getSubUserId() as string,
        })}`,
        resetScroll: false,
      });
    }
  }, [
    subUserIdQueryString,
    search,
    pathname,
    navigate,
    currentPlanId,
    prevPlanId,
  ]);

  return { mutateSubuser };
}
